/**
 * Типобезопасная обертка над localStorage
 *
 * Преимущества:
 * - Автоматическая сериализация/десериализация JSON
 * - Типизация ключей и значений
 * - Обработка ошибок (например, при превышении квоты)
 * - Централизованное управление хранилищем
 */

/**
 * Определение доступных ключей и их типов
 * Расширяйте этот интерфейс по мере необходимости
 */
interface StorageData {
	userPreferences: {
		theme: "light" | "dark"
		language: "ru" | "en"
		fontSize: "small" | "medium" | "large"
	}
	recentSearches: string[]
	lastVisitedPage: string
	// UI состояния для редактора тарифов
	tariffsEditorShowHelp: boolean
	tariffsEditorShowStats: boolean
}

type StorageKey = keyof StorageData

/**
 * Получить значение из localStorage
 */
function get<K extends StorageKey>(key: K): StorageData[K] | null {
	try {
		const item = localStorage.getItem(key)
		if (item === null) {
			return null
		}

		// Пытаемся распарсить как JSON
		try {
			return JSON.parse(item) as StorageData[K]
		} catch {
			// Если не JSON, возвращаем как есть (для строк)
			return item as StorageData[K]
		}
	} catch {
		return null
	}
}

/**
 * Сохранить значение в localStorage
 */
function set<K extends StorageKey>(key: K, value: StorageData[K]): boolean {
	try {
		const serialized = typeof value === "string" ? value : JSON.stringify(value)
		localStorage.setItem(key, serialized)
		return true
	} catch {
		return false
	}
}

/**
 * Удалить значение из localStorage
 */
function remove(key: StorageKey): void {
	try {
		localStorage.removeItem(key)
	} catch {
		// Ignore errors
	}
}

/**
 * Очистить весь localStorage
 */
function clear(): void {
	try {
		localStorage.clear()
	} catch {
		// Ignore errors
	}
}

/**
 * Проверить наличие ключа
 */
function has(key: StorageKey): boolean {
	return localStorage.getItem(key) !== null
}

/**
 * Получить все ключи
 */
function keys(): StorageKey[] {
	try {
		return Object.keys(localStorage) as StorageKey[]
	} catch {
		return []
	}
}

/**
 * Получить размер localStorage в байтах (приблизительно)
 */
function getSize(): number {
	try {
		let total = 0
		for (const key in localStorage) {
			if (localStorage.hasOwnProperty(key)) {
				const value = localStorage.getItem(key)
				total += key.length + (value?.length || 0)
			}
		}
		return total * 2 // 2 байта на символ в UTF-16
	} catch {
		return 0
	}
}

/**
 * Форматировать размер в человекочитаемый вид
 */
function formatSize(bytes: number): string {
	if (bytes === 0) return "0 Bytes"
	const k = 1024
	const sizes = ["Bytes", "KB", "MB"]
	const i = Math.floor(Math.log(bytes) / Math.log(k))
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

/**
 * Экспорт API для работы с localStorage
 */
export const storage = {
	get,
	set,
	remove,
	clear,
	has,
	keys,
	getSize,
	formatSize,
}

/**
 * In-memory storage для access token (безопаснее чем localStorage)
 * Token хранится только в памяти и не доступен для XSS атак
 */
let inMemoryAccessToken: string | null = null

/**
 * Типобезопасные хелперы для авторизации
 * Access token хранится в памяти, refresh token - в httpOnly cookie
 */
export const authStorage = {
	getAccessToken: () => inMemoryAccessToken,
	setAccessToken: (token: string) => {
		inMemoryAccessToken = token
	},
	removeAccessToken: () => {
		inMemoryAccessToken = null
	},
	clearAuth: () => {
		inMemoryAccessToken = null
	},
	hasToken: () => inMemoryAccessToken !== null,
}

export const preferencesStorage = {
	get: () =>
		storage.get("userPreferences") || {
			theme: "light",
			language: "ru",
			fontSize: "medium",
		},
	set: (prefs: StorageData["userPreferences"]) =>
		storage.set("userPreferences", prefs),
	update: (partial: Partial<StorageData["userPreferences"]>) => {
		const current = preferencesStorage.get()
		return storage.set("userPreferences", { ...current, ...partial })
	},
}
